import { FC, ReactNode, useEffect, useState } from 'react'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import { getAvatarPaciente } from '../../../../paciente-management/pacientes-list/core/_requests'
import {
  AnotacaoType,
  AtendimentoEstado,
  AtendimentoType,
  calcularIdade,
  IconPaths,
  IconSize,
  KTSVG,
  showAlert,
  showError,
  toAbsoluteUrl,
} from '../../../../../../../_metronic/helpers'
import { marcarPresencaPaciente } from '../../../../atendimento/core/_requests'
import { Button, CheckBox } from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'
import { ReplicasSolicitante, temReplica } from './ReplicasSolicitante'
import { CorrigirLotacaoSolicitante } from './CorrigirLotacaoSolicitante'
import { descreveLotacao } from 'app/modules/apps/funcionarios/funcionarios-list/core/descreveLotacao'

type Props = {
  atendimento: AtendimentoView | undefined
  isMarcarPresencaModal?: boolean
  setDisable?: (value: boolean) => void
  /** Recarrega o atendimento; sem ele, a correção da lotação do solicitante não é oferecida. */
  onAtualizar?: () => void
}

const ESTADO_BADGE_VARIANT: Partial<Record<AtendimentoEstado, string>> = {
  [AtendimentoEstado.Aguardando]: 'info',
  [AtendimentoEstado.EmAtendimento]: 'andamento',
  [AtendimentoEstado.Finalizado]: 'success',
  [AtendimentoEstado.Falta]: 'danger',
  [AtendimentoEstado.Cancelado]: 'danger',
  [AtendimentoEstado.RetornoPendencia]: 'warning',
  [AtendimentoEstado.Pendente]: 'warning',
}

type FieldProps = {
  label: string
  value?: ReactNode
}

const Field: FC<FieldProps> = ({ label, value }) => (
  <div>
    <p className='atd-field-label'>{label}</p>
    <p className='atd-field-value'>{value || '-'}</p>
  </div>
)

export const AtendimentoMedicoInfo: FC<Props> = ({
  isMarcarPresencaModal = false,
  setDisable,
  atendimento,
  onAtualizar,
}) => {
  const { t } = useT()

  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(false)
  const [avatar, setAvatar] = useState<string>('/media/avatars/AVATARES_05.png')
  const [presenca, setPresenca] = useState(false)

  const pacienteId = atendimento?.paciente?.id

  const handleMarcarPresenca = async () => {
    try {
      await marcarPresencaPaciente(atendimento?.id)
      setPresenca(true)
      showAlert({ title: 'Sucesso', text: 'Presença Marcada com sucesso!', icon: 'success' })
    } catch (error) {
      showError(error)
    }
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(event.target.checked)
    setDisable?.(!event.target.checked)
  }

  useEffect(() => {
    const getImagemAvatar = async () => {
      try {
        const avatar = await getAvatarPaciente(pacienteId)
        if (avatar) setAvatar(avatar)
      } catch (error) {
        showError(error)
      }
    }
    getImagemAvatar()
  }, [pacienteId])

  const pendencias = atendimento?.anotacoes?.filter(
    (anotacao) => anotacao.tipo === AnotacaoType.Pendencia
  )

  const isCancelado = atendimento?.estado?.id === AtendimentoEstado.Cancelado
  const falta = atendimento?.estado?.id === AtendimentoEstado.Falta
  const isOcupacional = atendimento?.tipo === AtendimentoType.Ocupacional
  // Parecer é entre profissionais: o paciente não comparece, não há presença a confirmar.
  const isInterconsulta = atendimento?.tipo === AtendimentoType.Interconsulta
  const presencaConfirmada = !!atendimento?.presencaPacienteFormatada || presenca

  const observacoes = atendimento?.anotacoes?.filter(
    (anotacao) => anotacao.tipo === AnotacaoType.Observacao
  )

  const badgeVariant =
    ESTADO_BADGE_VARIANT[atendimento?.estado?.id as AtendimentoEstado] ?? 'secondary'

  return (
    <div>
      <div className='atd-identity flex-wrap'>
        <div className='atd-avatar'>
          <img alt='Avatar do paciente' src={toAbsoluteUrl(avatar)} />
        </div>
        <div className='d-flex flex-column flex-grow-1'>
          <div className='d-flex align-items-baseline gap-3 flex-wrap mb-1'>
            <span className='fs-3 fw-bolder text-gray-900'>{atendimento?.paciente?.nome}</span>
            {atendimento?.paciente?.id && (
              <a
                href={`/apps/atendimentosMedicos/historicoClinico/${atendimento.paciente.id}`}
                target='_blank'
                rel='noopener noreferrer'
                className='atd-link'
              >
                Ver histórico
                <span>&#8599;</span>
              </a>
            )}
          </div>
          <div className='d-flex flex-wrap align-items-center gap-2 text-muted fs-7 fw-semibold'>
            {atendimento?.paciente?.pessoa?.sexo && <span>{atendimento.paciente.pessoa.sexo}</span>}
            {atendimento?.paciente?.pessoa?.dataNascimentoFormatada && (
              <>
                <span className='atd-meta-sep' />
                <span>
                  {calcularIdade(atendimento.paciente.pessoa.dataNascimento as string)} anos ·{' '}
                  {atendimento.paciente.pessoa.dataNascimentoFormatada}
                </span>
              </>
            )}
            {atendimento?.paciente?.pessoa?.cpf && (
              <>
                <span className='atd-meta-sep' />
                <span>CPF {atendimento.paciente.pessoa.cpf}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-body p-4'>
          <div className='atd-panel atd-grid'>
            <Field label={t('empresa.label')} value={atendimento?.empresa?.nomeFantasia} />
            {atendimento?.unidade && (
              <Field label='Unidade/Setor' value={atendimento.unidade.nome} />
            )}
            <Field label='Data do Agendamento' value={atendimento?.solicitacaoFormatada} />
            {atendimento?.estado?.descricao && (
              <div>
                <p className='atd-field-label'>Estado</p>
                <span className={`atd-badge atd-badge-${badgeVariant}`}>
                  <span className='atd-badge-dot' />
                  {atendimento.estado.descricao}
                </span>
              </div>
            )}
            <Field label='Protocolo' value={atendimento?.protocolo} />
            <Field label='Retorno' value={atendimento?.retorno ? 'Sim' : 'Não'} />
            {/* Na interconsulta quem importa é o solicitante, que pode não ser quem criou o registro. */}
            {isInterconsulta ? (
              <Field label='Solicitante' value={atendimento?.solicitante?.nome} />
            ) : (
              <Field label='Criado por' value={atendimento?.criador?.nome} />
            )}
            {atendimento?.lotacaoSolicitante && (
              <Field
                label='Lotação do solicitante'
                value={descreveLotacao(atendimento.lotacaoSolicitante)}
              />
            )}
          </div>

          {isInterconsulta && !atendimento?.lotacaoSolicitante && onAtualizar && (
            <CorrigirLotacaoSolicitante atendimento={atendimento} onCorrigida={onAtualizar} />
          )}

          {isOcupacional && (
            <>
              <div className='separator my-5' />
              <div className='atd-subhead'>Dados Ocupacionais</div>
              <div className='atd-grid mb-4'>
                <Field label='Cargo' value={atendimento.dadosAso?.cargo} />
                <Field label='Setor' value={atendimento.dadosAso?.setor} />
                <Field label={`${t('empresa.label')} cliente`} value={atendimento?.cliente?.nome} />
                <Field label='Tipo ASO' value={atendimento?.tipoAso} />
              </div>
            </>
          )}

          {isOcupacional &&
            (!!atendimento?.fatoresRisco?.length || !!atendimento?.procedimentos?.length) && (
              <>
                <div className='separator my-5' />
                <div className='atd-cols-2'>
                  {!!atendimento?.fatoresRisco?.length && (
                    <div>
                      <div className='atd-subhead'>Riscos</div>
                      <div className='d-flex flex-wrap gap-2'>
                        {atendimento.fatoresRisco.map((risco, index) => (
                          <span key={index} className='atd-chip atd-chip-warning'>
                            {risco.descricaoFatorRisco}: {risco.valor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {!!atendimento?.procedimentos?.length && (
                    <div>
                      <div className='atd-subhead'>Exames</div>
                      <div className='d-flex flex-wrap gap-2'>
                        {atendimento.procedimentos.map((p, index) => (
                          <span key={index} className='atd-chip atd-chip-primary'>
                            {!!p?.descricao ? `${p.nome}: ${p.descricao}` : p.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          {(isCancelado || falta) && observacoes && observacoes.length > 0 && (
            <>
              <div className='separator my-5' />
              <div>
                <div className='atd-alert atd-alert-danger'>
                  <div className='atd-alert-title'>
                    <KTSVG path={IconPaths.Info} className='svg-icon-2 svg-icon-danger' />
                    Observação
                  </div>
                  {observacoes.map((anotacao, index) => (
                    <div className='atd-alert-entry' key={index}>
                      <div className='atd-alert-body'>{anotacao.anotacao}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {temReplica(atendimento) && (
            <>
              <div className='separator my-5' />
              <div>
                <ReplicasSolicitante atendimento={atendimento} />
              </div>
            </>
          )}

          {pendencias && pendencias.length > 0 && (
            <>
              <div className='separator my-5' />
              <div>
                <div className='atd-alert atd-alert-warning'>
                  <div className='atd-alert-title'>
                    <KTSVG path={IconPaths.Info} className='svg-icon-2 svg-icon-warning' />
                    Atendimento pendente · {pendencias.length}{' '}
                    {pendencias.length > 1 ? 'motivos' : 'motivo'}
                  </div>
                  {pendencias.map((anotacao, index) => (
                    <div className='atd-alert-entry' key={index}>
                      <div className='atd-alert-meta'>
                        {anotacao.dataFormatada} — {anotacao.usuario?.nome}
                      </div>
                      <div className='atd-alert-body'>{anotacao.anotacao}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {!isInterconsulta && (
            <div>
              <div className='separator my-5' />
              {presencaConfirmada ? (
                <div className='atd-success'>
                  <KTSVG
                    path={IconPaths.Check}
                    className='svg-icon-success'
                    size={IconSize.Small}
                  />
                  <span className='fw-bold text-success'>Presença do paciente confirmada!</span>
                </div>
              ) : (
                <div className='atd-confirm form-check form-check-custom'>
                  <CheckBox
                    id='presencaPaciente'
                    checked={isCheckboxChecked}
                    onChange={handleCheckboxChange}
                  />
                  <label className='form-check-label fw-bold text-dark' htmlFor='presencaPaciente'>
                    Confirmo que conferi os dados do paciente e a veracidade das informações.
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isCheckboxChecked && !isMarcarPresencaModal && (
        <div className='d-flex justify-content-end'>
          <Button className='btn-success' onClick={handleMarcarPresenca}>
            Confirmar presença do paciente
          </Button>
        </div>
      )}
    </div>
  )
}
